import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RecentlyLaunchedToken {
  id: string;
  mintAddress: string;
  name: string;
  symbol: string;
  imageUrl: string | null;
  createdAt: Date;
  poolAddress: string | null;
  marketCapUsd: number | null;
}

async function fetchRecentlyLaunched(limit: number): Promise<RecentlyLaunchedToken[]> {
  const { data, error } = await supabase
    .from('launched_tokens')
    .select('id, mint_address, name, symbol, image_url, created_at, pool_address')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[useRecentlyLaunched] Error:', error);
    throw error;
  }

  if (!data || data.length === 0) return [];

  // Fetch latest market cap for each token from price history
  const mints = data.map((t) => t.mint_address);
  const { data: priceData } = await supabase
    .from('token_price_history')
    .select('token_address, market_cap_usd')
    .in('token_address', mints)
    .order('recorded_at', { ascending: false });

  // Build map: mint -> latest market_cap_usd (first match since ordered DESC)
  const mcMap = new Map<string, number>();
  if (priceData) {
    for (const row of priceData as { token_address: string; market_cap_usd: number | null }[]) {
      if (row.market_cap_usd != null && !mcMap.has(row.token_address)) {
        mcMap.set(row.token_address, row.market_cap_usd);
      }
    }
  }

  return data.map((t) => ({
    id: t.id,
    mintAddress: t.mint_address,
    name: t.name,
    symbol: t.symbol,
    imageUrl: t.image_url,
    createdAt: new Date(t.created_at),
    poolAddress: t.pool_address,
    marketCapUsd: mcMap.get(t.mint_address) ?? null,
  }));
}

export function useRecentlyLaunched(limit = 5) {
  const query = useQuery({
    queryKey: ['recently-launched', limit],
    queryFn: () => fetchRecentlyLaunched(limit),
    staleTime: 30 * 1000, // Tier 2
    gcTime: 5 * 60 * 1000, // Tier 2
    refetchInterval: 60 * 1000,
    placeholderData: keepPreviousData,
  });

  return {
    tokens: query.data || [],
    isLoading: query.isLoading,
  };
}
